import React, { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { OTP_LOGIN_ISSUE_URL } from 'shared-modules/config';
import { BUTTON_ROLES } from 'shared-modules/constants';
import { useKeyPressHandler } from '../../services/hooks';
import { otpVerifyRequest, resetLoginError } from '../../redux/actions';
import CustomInput from '../../components/CustomInput';
import CustomButton from '../../components/CustomButton';
import styles from './loginAuthPage.module.scss';
import triautoLogo from '../../assets/login/logo.svg';

const LoginAuthPage = ({ login, onCancel, onSubmit }) => {
  const dispatch = useDispatch();
  const [otpCode, setOtpCode] = useState('');
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const authIsLoading = useSelector((state) => state.auth.isOtpLoading);
  const error = useSelector((state) => state.auth.error);
  const errorMessages = useSelector((state) => state.auth.errorMessages);
  const loginErrorMessage = useSelector((state) => state.auth.loginErrorMessage);
  const handleResetOtpCodeError = useCallback(() => dispatch(resetLoginError({ name: 'otpCode' })), [dispatch]);
  const loginQuestionTab = useCallback(() => window.open(OTP_LOGIN_ISSUE_URL, '_blank'), []);
  const loginQuestionTabByKey = useKeyPressHandler(loginQuestionTab);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!login || !otpCode || authIsLoading || otpSubmitting || error) {
        return;
      }

      handleResetOtpCodeError();

      onSubmit?.();

      setOtpSubmitting(true);
      if (otpCode && !authIsLoading) {
        dispatch(
          otpVerifyRequest({
            login,
            otpCode,
            successCallback: () => {
              setOtpSubmitting(false);
            },
            errorCallback: () => {
              setOtpSubmitting(false);
            },
          }),
        );
      }
    },
    [otpCode, authIsLoading, dispatch, error, handleResetOtpCodeError, login, onSubmit, otpSubmitting],
  );

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.containerLeft}>
          <div className={styles.logoWrapper}>
            <img src={triautoLogo} className={styles.logo} alt="トライオート" />
          </div>
        </div>
        <div className={styles.containerRight}>
          <div className={styles.formContainer}>
            <div className={styles.titleSection}>
              <div className={styles.title}>ログイン認証</div>
              <div className={styles.subTitle}>
                メールをご確認いただき、記載されている認証コードを入力してください。
              </div>
            </div>
            <div className={styles.formSection}>
              <form onSubmit={handleSubmit} autoComplete="off">
                <div className={styles.inputGroup}>
                  <div className={styles.inputSection}>
                    <CustomInput
                      label="認証コード"
                      value={otpCode}
                      onChange={(value) => {
                        setOtpCode(value?.replace(/[^0-9]/g, ''));
                      }}
                      name="otpCode"
                      resetError={handleResetOtpCodeError}
                      labelClassName={styles.inputLabel}
                      errorMessages={errorMessages}
                      inputClassName={styles.input}
                      errorClassName={styles.codeError}
                      maxLength={6}
                    />
                  </div>
                  {loginErrorMessage && (
                    // カウリス側で画面要素を取得するため、idを付与
                    <div id="loginErrorMessageOtp" className={styles.emergencyText}>
                      {loginErrorMessage}
                    </div>
                  )}
                  <div className={styles.buttonGroup}>
                    <CustomButton
                      isDisabled={!otpCode || error || authIsLoading || otpSubmitting}
                      isLoading={authIsLoading}
                      buttonRole={BUTTON_ROLES.SUBMIT}
                      buttonClassName={styles.submitButton}
                    >
                      認証
                    </CustomButton>
                    <CustomButton
                      isDisabled={authIsLoading || otpSubmitting}
                      buttonClassName={styles.cancelButton}
                      onClick={handleCancel}
                    >
                      キャンセル
                    </CustomButton>
                  </div>
                </div>
              </form>
              <div className={styles.noteText}>※認証コードの有効期限は5分です。</div>
              <div className={styles.helpLinkWrapper}>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={loginQuestionTab}
                  onKeyPress={loginQuestionTabByKey}
                  className={styles.helpLink}
                >
                  ログイン認証でお困りの方
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

LoginAuthPage.propTypes = {
  login: PropTypes.string.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
};

LoginAuthPage.defaultProps = {
  onCancel: undefined,
  onSubmit: undefined,
};

export default memo(LoginAuthPage);
